import React from "react";
import { StoreCategory } from "tsi-common-react/src/api/retail";
import { useAppDispatch } from "tsi-common-react/src/apps/reducers";
import { updateStoreFilters } from "tsi-common-react/src/apps/retail/reducers";
import { IProps as IModalProps } from "tsi-common-react/src/common/Modal";
import {
    PopperOptions,
    UncontrolledPopover,
} from "tsi-common-react/src/common/Popover";
import { Trans } from "tsi-common-react/src/common/Trans";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { isoMiles } from "tsi-common-react/src/models/nominals";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import tabnavAll from "../../img/retail-locator/tabnav-all.jpg";
import tabnavClosest5 from "../../img/retail-locator/tabnav-closest5.png";
import tabnavElite from "../../img/retail-locator/tabnav-elite.png";
import tabnavFlagship from "../../img/retail-locator/tabnav-flagship.jpg";
import { LocatorQuickSearch as LocatorQuickSearchComponent } from "../components/LocatorQuickSearch";
import { QuickSearchItem } from "../models";

const flagshipTooltipText = t`Choose from the widest assortment of genuine
    Tempur-Pedic products to discover your perfect bed with assistance from our
    highly experienced sleep experts.`;
const eliteTooltipText = t`Tempur-Pedic Elite Retailers offer a broad
    selection of Tempur-Pedic mattresses and adjustable bases. They are
    recognized for their comprehensive product knowledge, extraordinary
    service, and high level of customer satisfaction.`;

const popperOptions: PopperOptions = {
    modifiers: [
        {
            name: "offset",
            options: {
                offset: [0, 16],
            },
        },
        {
            name: "flip",
            options: {
                fallbackPlacements: ["top", "bottom"],
            },
        },
    ],
    placement: "bottom",
};

const modalProps: Partial<IModalProps> = {
    bodyOpenClassName: "tooltip-component__container",
    overlayClassName: "tooltip-component__overlay",
    className: "tooltip-component",
    contentLabel: t`More Info`,
    style: {
        overlay: {
            backgroundColor: "rgba(255, 255, 255, 0)",
        },
        content: {
            border: "none",
            boxShadow: "none",
        },
    },
};

const items: QuickSearchItem[] = [
    {
        id: "flagship",
        title: (
            <>
                Tempur-Pedic<sup>®</sup> Stores
            </>
        ),
        descr: (
            <Trans
                fmtString={t`<Popover>Tempur-Pedic Stores</Popover> near you.`}
                data={{
                    Popover: (content) => (
                        <UncontrolledPopover
                            key="Popover"
                            triggerContent={content}
                            popperOptions={popperOptions}
                            modalProps={modalProps}
                            showArrow={true}
                        >
                            {flagshipTooltipText}
                        </UncontrolledPopover>
                    ),
                }}
            />
        ),
        labelClassName: "quicksearch__nav-button--tpstore",
        imgSrc: tabnavFlagship,
        imgAlt: t`Tempur-Pedic® Stores`,
        href: "/tempur-pedic-stores/", // TODO: Fix this URL
        defaultFilterState: {
            limit: 0,
            distance: isoMiles.wrap(0),
            category: StoreCategory.FLAGSHIP,
            retailerName: [],
        },
    },
    {
        id: "closest-5",
        title: t`Closest 5 Retailers`,
        descr: t`Closest five retailers based on your location.`,
        labelClassName: "quicksearch__nav-button--closest",
        imgSrc: tabnavClosest5,
        imgAlt: t`Closest 5 Retailers`,
        href: "/find-a-retailer/#closest-5", // TODO: Fix this URL
        defaultFilterState: {
            limit: 5,
            category: null,
            retailerName: [],
        },
    },
    {
        id: "elite",
        title: t`Elite Retailers`,
        descr: (
            <Trans
                fmtString={t`Find an <Popover>Elite</Popover> retailer near you.`}
                data={{
                    Popover: (content) => (
                        <UncontrolledPopover
                            key="Popover"
                            triggerContent={content}
                            popperOptions={popperOptions}
                            modalProps={modalProps}
                            showArrow={true}
                        >
                            {eliteTooltipText}
                        </UncontrolledPopover>
                    ),
                }}
            />
        ),
        labelClassName: "quicksearch__nav-button--elite",
        imgSrc: tabnavElite,
        imgAlt: t`Elite Retailers`,
        href: "/find-a-retailer/#elite", // TODO: Fix this URL
        defaultFilterState: {
            limit: 0,
            category: StoreCategory.ELITE,
            retailerName: [],
        },
    },
    {
        id: "all",
        title: t`All Retailers`,
        descr: t`All retailers in your area.`,
        labelClassName: "quicksearch__nav-button--all",
        imgSrc: tabnavAll,
        imgAlt: t`All Retailers`,
        href: "/find-a-retailer/#all", // TODO: Fix this URL
        defaultFilterState: {
            limit: 0,
            category: null,
            retailerName: [],
        },
    },
];

const getItems = (hideElite: boolean | undefined): QuickSearchItem[] => {
    if (hideElite) {
        return items.filter((item) => item.id !== "elite");
    }
    return items;
};

const handleHashChange = (
    dispatch: ReturnType<typeof useAppDispatch>,
    hideElite: boolean | undefined,
    setHash: (hash: string) => void,
): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    const hash = window.location.hash.replace("#", "") || "closest-5";
    setHash(hash);
    const filteredItems = getItems(hideElite);
    const selectedItem = filteredItems.find((item) => item.id === hash);
    if (selectedItem) {
        dispatch(updateStoreFilters(selectedItem.defaultFilterState));
    }
};

export const LocatorQuickSearch = (props: {
    selectedItem: string | undefined;
    hideElite?: boolean;
}): React.ReactNode => {
    const dispatch = useAppDispatch();
    const [hash, setHash] = React.useState("");

    useMountEffect(() => {
        const onHashChange = () => {
            handleHashChange(dispatch, props.hideElite, setHash);
        };

        const window = getWindow();
        if (!window) {
            return;
        }

        onHashChange();
        window.addEventListener("hashchange", onHashChange);

        return () => {
            window.removeEventListener("hashchange", onHashChange);
        };
    });

    const selectedItem = props.selectedItem || hash.replace("#", "");
    return (
        <LocatorQuickSearchComponent
            items={getItems(props.hideElite)}
            selectedItem={selectedItem}
        />
    );
};
